import React, { useMemo, memo, useCallback, useEffect } from 'react';

import moment, { duration } from 'moment';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Button from '@mui/material/Button';
import ListItem from '@mui/material/ListItem';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { axiosConfig } from '../../utils/axiosConfig';
import { getCookie } from '../../App';
import { BACKEND_URL } from '../../constans';

import IconRightArrow from '../../assents/img/rigthArrow.svg';

const Wrapper = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '380px',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    [theme.breakpoints.up('sm')]: {
        width: '390px',
        height: '100vh',
        position: 'sticky',
        top: 0,
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
    },
}));

const SubWrapper = styled(Box)(({ theme }) => ({
    width: '375px',
    height: '380px',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',
    padding: '31px 20px 24px',
    [theme.breakpoints.up('sm')]: {
        height: '100vh',
        width: '100%',
        padding: '36px 36px 15px 36px',
    },
}));

const Title = styled(Typography)(({ theme }) => ({
    fontFamily: 'Proxima Nova',
    fontWeight: 'bold',
    fontSize: '16px',
    lineHeight: 1.187,
    color: '#1a1a1a',
    [theme.breakpoints.up('sm')]: {
        fontSize: '14px',
        lineHeight: 1.214,
    },
}));

const SubTitle = styled(Typography)(() => ({
    marginTop: '5px',
    fontFamily: 'Proxima Nova',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: 1.214,
    color: '#1a1a1a',
    opacity: 0.5,
}));

const Hr = styled('hr')(() => ({
    opacity: 0.1,
    marginTop: '23px',
    marginBottom: '16px',
}));

const TotalList = styled(List)(() => ({
    padding: 0,
}));

const TotalListItem = styled(ListItem)(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 0,
    '&:not(:last-child)': { marginBottom: '10px' },
}));

const TotalOption = styled(Typography)(() => ({
    fontFamily: 'Proxima Nova',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: 1.214,
    color: '#1a1a1a',
}));

const TotalValue = styled(Typography)(() => ({
    fontFamily: 'Proxima Nova',
    fontWeight: 'bold',
    fontSize: '14px',
    lineHeight: 1.214,
    color: '#1a1a1a',
}));

const TotalText = styled(Typography)(({ theme }) => ({
    marginTop: '19px',
    textAlign: 'center',
    fontFamily: 'Proxima Nova',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: 1.214,
    color: 'rgba(0, 0, 0, 0.5)',
    [theme.breakpoints.up('sm')]: {
        display: 'none',
    },
}));

const BookWrapper = styled(Box)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.up('sm')]: {
        display: 'block',
        padding: 0,
        margin: 0,
        flexDirection: 'column',
        position: 'absolute',
        bottom: '16px',
        textAlign: 'center',
        border: 'none',
    },
}));

const BookButton = styled(Button)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '22.5px 26px',
    background: '#1a1a1a',
    borderRadius: '8px',
    border: 'none',
    boxSizing: 'border-box',
    textTransform: 'none',
    fontFamily: 'Proxima Nova',
    fontWeight: 'bold',
    fontSize: '16px',
    lineHeight: 1.187,
    color: '#ffffff',
    cursor: 'pointer',
    '&:hover': {
        background: '#1a1a1a',
    },
    [theme.breakpoints.up('sm')]: {
        margin: '0px auto',
        padding: '22.5px 60px',
    },
}));

const RightArrow = styled('img')(() => ({
    marginLeft: '10px',
}));

const BookText = styled(Typography)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.up('sm')]: {
        display: 'block',
        width: '232px',
        marginTop: '15px',
        fontFamily: 'Proxima Nova',
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: 1.25,
        color: 'rgba(0, 0, 0, 0.5)',
    },
}));

const ButtonTotalText = styled('span')(({ theme }) => ({
    display: 'block',
    marginLeft: '5px',
    [theme.breakpoints.up('sm')]: {
        display: 'none',
    },
}));

const TotalSection = memo(
    ({
        setDateNotEmpty,
        setTimeNotEmpty,
        currentRouteName,
        setDropOffNotEmpty,
        setLocationNotEmpty,
        dropOffLocation,
        totalMinutes,
        addedStops,
        total,
        tourDefaultPrice,
        slug,
        date,
        time,
        tourId,
        pickUpLocation,
        checkboxIsCheked,
        setTotal,
        setDate,
    }) => {
        const totalPrice = useMemo(() => {
            return (
                addedStops.reduce(
                    (acc, el) =>
                        acc +
                        (Number(Number(el.price_adult).toFixed(0)) * total.adults +
                            Number(Number(el.price_child).toFixed(0)) * total.children +
                            Number(Number(el.price_infant).toFixed(0)) * total.infants) +
                        el.duration * el.price_minute,
                    0
                ) + Number(Number(tourDefaultPrice).toFixed(0))
            );
        }, [addedStops, total, tourDefaultPrice]);

        const totalDuration = useMemo(() => {
            const totalDuration = totalMinutes;
            const duration = moment.duration(totalDuration, 'minutes');
            const hours = duration.hours();
            const minutes = duration.minutes();

            return `${hours === 0 ? '' : hours === 1 ? `${hours} hour` : `${hours} hours`} ${
                minutes === 0 ? '' : `${minutes} minutes`
            }`;
        }, [totalMinutes, addedStops]);

        const totalPeople = useMemo(() => {
            const totalAbults = `${
                total.adults > 0 ? (total.adults > 1 ? `${total.adults} adults` : `${total.adults} adult`) : ''
            }`;
            const totalChild = `${total.children > 0 ? `${total.children} child` : ''}`;
            const totalInfants = `${
                total.infants > 0 ? (total.infants > 1 ? `${total.infants} infants` : `${total.infants} infant`) : ''
            }`;

            const totalString = `${totalAbults}${
                (totalAbults.length > 0 && totalChild.length > 0) || (totalAbults.length > 0 && totalInfants.length > 0)
                    ? ','
                    : ''
            } ${totalChild}${totalChild.length > 0 && totalInfants.length > 0 ? ',' : ''} ${totalInfants}`;

            return totalString.trim() !== '' ? totalString : '-';
        }, [total]);

        const getTimeDuration = useMemo(() => {
            const endTime = moment(time, 'hhmm')
                .add(totalMinutes / 60, 'hours')
                .format('HH:mm');
            return `${time} - ${endTime}`;
        }, [totalMinutes, time, addedStops]);

        const sendCookies = useCallback(
            async (
                date,
                time,
                adults_people,
                child_people,
                infant_people,
                language,
                total_cost,
                places_id,
                is_private,
                tour_route_id,
                pick_up,
                full_duration,
                drop_off,
                is_pick_and_drop
            ) => {
                try {
                    const response = await axiosConfig({
                        method: 'post',
                        url: `tour-api/send-data-from-cookies/`,
                        data: {
                            date,
                            time,
                            adults_people,
                            child_people,
                            infant_people,
                            language,
                            total_cost,
                            places_id,
                            is_private,
                            tour_route_id,
                            pick_up,
                            full_duration,
                            drop_off,
                            is_pick_and_drop,
                        },
                        headers: {
                            'X-CSRFToken': getCookie('csrftoken'),
                        },
                    });
                } catch (err) {
                    console.log(err);
                }
            },
            [
                date,
                dropOffLocation,
                time,
                total,
                totalPrice,
                addedStops,
                slug,
                tourId,
                pickUpLocation,
                checkboxIsCheked,
                totalMinutes,
            ]
        );

        const handleSubmit = useCallback(async () => {
            if (pickUpLocation.trim().length === 0 || pickUpLocation.trim().length < 15) {
                if (!checkboxIsCheked) {
                    if (dropOffLocation.trim().length === 0 || dropOffLocation.trim().length < 15) {
                        setDropOffNotEmpty(true);
                    }
                }
                if (!date) {
                    setDateNotEmpty(true);
                }

                if (!time) {
                    setTimeNotEmpty(true);
                }
                setLocationNotEmpty(true);
                return;
            }
            if (!checkboxIsCheked) {
                if (dropOffLocation.trim().length === 0 || dropOffLocation.trim().length < 15) {
                    if (!date) {
                        setDateNotEmpty(true);
                    }

                    if (!time) {
                        setTimeNotEmpty(true);
                    }
                    setDropOffNotEmpty(true);
                    return;
                }
            }

            if (!date) {
                if (!time) {
                    setTimeNotEmpty(true);
                }
                setDateNotEmpty(true);
                return;
            }

            if (!time) {
                setTimeNotEmpty(true);
                return;
            }

            try {
                const response = await sendCookies(
                    moment(date).format('MMMM DD, YYYY'),
                    time.length > 0 ? time : '10:00',
                    total.adults,
                    total.children,
                    total.infants,
                    'English',
                    totalPrice,
                    addedStops.map((el) => el.id).join(','),
                    'true',
                    tourId,
                    pickUpLocation,
                    totalMinutes,
                    checkboxIsCheked ? pickUpLocation : dropOffLocation,
                    checkboxIsCheked ? 'true' : 'false'
                );

                document.cookie = `date=${moment(date).format('MMMM DD, YYYY')}; path=/`;
                document.cookie = `time=${time.length > 0 ? time : '10:00'}; path=/`;
                document.cookie = `adult_people=${total.adults}; path=/`;
                document.cookie = `child_people=${total.children}; path=/`;
                document.cookie = `infant_people=${total.infants}; path=/`;
                document.cookie = 'language=English; path=/';
                document.cookie = `total=${totalPrice}; path=/`;
                document.cookie = `places_id=${addedStops.map((el) => el.id).join(',')}; path=/`;
                document.cookie = 'is_private=true; path=/';
                document.cookie = `tour_route_id=${tourId}; path=/`;
                document.cookie = `pick_up=${pickUpLocation}; path=/`;
                document.cookie = `full_duration=${totalMinutes}; path=/`;
                document.cookie = `drop_off=${checkboxIsCheked ? pickUpLocation : dropOffLocation}; path=/`;
                document.cookie = `is_pick_and_drop=${checkboxIsCheked}; path=/`;
                window.location.href = `${BACKEND_URL}booking/info/${slug}/`;
            } catch (error) {
                console.log(error);
            }
        }, [
            date,
            dropOffLocation,
            time,
            total,
            totalPrice,
            addedStops,
            slug,
            tourId,
            pickUpLocation,
            checkboxIsCheked,
            totalMinutes,
            sendCookies,
        ]);

        const { childrenCookie, adultsCookie, infantsCookie, dataCookie } = useMemo(() => {
            return {
                childrenCookie: document.cookie.includes('children_people_from_django')
                    ? +document.cookie.match(/children_people_from_django=\d+/)[0].slice(-1)
                    : 0,
                infantsCookie: document.cookie.includes('infants_people_from_django')
                    ? +document.cookie.match(/infants_people_from_django=\d+/)[0].slice(-1)
                    : 0,
                adultsCookie: document.cookie.includes('adults_people_from_django')
                    ? +document.cookie.match(/adults_people_from_django=\d+/)[0].slice(-1)
                    : 0,
                dataCookie: document.cookie.match(/data_from_django=.+;/)
                    ? document.cookie.match(/data_from_django=.+;/)[0].slice(17, 32)
                    : null,
            };
        }, [document.cookie]);

        useEffect(() => {
            if (childrenCookie > 0 || infantsCookie > 0 || adultsCookie > 0) {
                setTotal({
                    ...total,
                    children: childrenCookie ? childrenCookie : 0,
                    infants: infantsCookie ? infantsCookie : 0,
                    adults: adultsCookie ? adultsCookie : 1,
                });
            }
            if (dataCookie) {
                setDate(moment(dataCookie).format('MMMM DD, YYYY'));
            }
        }, []);

        return (
            <Wrapper>
                <SubWrapper>
                    <Title>{currentRouteName}</Title>
                    <SubTitle>Summary</SubTitle>
                    <Hr />
                    <TotalList>
                        <TotalListItem>
                            <TotalOption>Date:</TotalOption>
                            <TotalValue>
                                {moment(date).format('MMMM DD, YYYY') === 'Invalid date'
                                    ? 'Not selected'
                                    : moment(date).format('MMMM DD, YYYY')}
                            </TotalValue>
                        </TotalListItem>
                        <TotalListItem>
                            <TotalOption>Time:</TotalOption>
                            <TotalValue>{time.length > 0 ? getTimeDuration : 'Not selected'}</TotalValue>
                        </TotalListItem>
                        <TotalListItem>
                            <TotalOption>Total duration:</TotalOption>
                            <TotalValue>{totalDuration}</TotalValue>
                        </TotalListItem>
                        <TotalListItem>
                            <TotalOption>Stops:</TotalOption>
                            <TotalValue>{addedStops.length}</TotalValue>
                        </TotalListItem>
                        <TotalListItem>
                            <TotalOption>People:</TotalOption>
                            <TotalValue>{totalPeople}</TotalValue>
                        </TotalListItem>
                        <TotalListItem>
                            <TotalOption>Language:</TotalOption>
                            <TotalValue>English</TotalValue>
                        </TotalListItem>
                    </TotalList>
                    <Hr />
                    <Box>
                        <TotalList>
                            <TotalListItem>
                                <TotalOption>Total price:</TotalOption>
                                <TotalValue sx={{ color: '#2BB656' }}>{Math.ceil(totalPrice)}€</TotalValue>
                            </TotalListItem>
                        </TotalList>
                        <TotalText>You’ll be able to review everything in the next step.</TotalText>
                    </Box>
                    <BookWrapper>
                        <BookButton onClick={handleSubmit}>
                            Book now <ButtonTotalText>€{Math.ceil(totalPrice)}</ButtonTotalText>
                            <RightArrow src={IconRightArrow} />
                        </BookButton>
                        <BookText>
                            Don’t worry, you’ll be able to review everything after clicking this button.
                        </BookText>
                    </BookWrapper>
                </SubWrapper>
            </Wrapper>
        );
    }
);

export default TotalSection;
